import React from 'react';
import { useTheme } from '@mui/system';
import { useMediaQuery } from '@mui/material';
import dateFormat from 'dateformat';
import { isThisYear } from 'date-fns';
import { createTitle } from '#component/shipment/form/cargo/cargo-item/helper';
import { formatter } from '#component/shipment/helper';
import LogInlineLink from '#component/general/log-inline-link';
import { shipmentEventType } from '#type';
import { getDeviceUrl, getDateTimeFormat, momentFormat } from '#helper';

const EventDescription = ({ log, account, isEventTab }) => {
  const {
    shipment,
    device,
    delivery,
    cargo,
    file,
    alert,
    event,
    timestamp,
    notification = {},
    data
  } = log;

  const {
    CREATE_SHIPMENT,
    EDIT_SHIPMENT,
    DELETE_SHIPMENT,
    STOP_SHIPMENT,
    DONE_SHIPMENT,
    CREATE_DEEPLINK,
    NOT_IN_TIME_SHIPMENT,
    ADD_DEVICE,
    REMOVE_DEVICE,
    ADD_DELIVERY,
    EDIT_DELIVERY,
    REMOVE_DELIVERY,
    DONE_CHECKPOINT,
    NOT_IN_TIME_CHECKPOINT,
    ADD_CARGO,
    EDIT_CARGO,
    REMOVE_CARGO,
    UPLOAD_FILE,
    REMOVE_FILE,
    ADD_ALERT,
    REMOVE_ALERT,
    TRIGGER_ALERT,
    ASSIGN_ALERT_TO_DEVICE,
    UNASSIGN_ALERT_FROM_DEVICE,
    ADD_NOTIFICATION,
    EDIT_NOTIFICATION,
    REMOVE_NOTIFICATION,
    ADD_ALERT_CONFIRMATION,
    EDIT_ALERT_CONFIRMATION,
    REMOVE_ALERT_CONFIRMATION
  } = shipmentEventType;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  try {
    const cargoTitleLabel = `${createTitle(cargo?.goods, cargo?.container).trim()}`;
    const cargoTitle = cargoTitleLabel ? (
      <>
        &quot;
        {cargoTitleLabel}
        &quot;
      </>
    ) : ' ';
    const shipmentTitle = shipment?.label ? `${shipment.label}` : formatter(shipment.uid);
    const shipmentLabel = isEventTab ? '' : (
      <LogInlineLink to={`/shipment/${shipment.id}`} text={shipmentTitle} isMobile={isMobile} />
    );
    const triggeredInShipmentLabel = shipmentLabel ? (
      <>
        in shipment
        {' '}
        {shipmentLabel}
      </>
    ) : ' ';
    const deviceTitle = `${device?.title || device?.uid}`;
    const deviceUrl = getDeviceUrl(device?.id, timestamp);
    const deviceLabel = (
      <LogInlineLink to={{ pathname: deviceUrl }} text={deviceTitle} isMobile={isMobile} />
    );
    const fileName = `"${file?.name}"`;
    const alertLabel = `"${alert?.label}"`;

    const eventId = event.id;

    const recipientObj = notification.addressbookContact || notification.user;
    const recipientName = recipientObj ? `"${recipientObj.firstName} ${recipientObj.lastName}"` : `"${notification.email}"`;
    const deeplink = `${window.location.origin}/link/shipment/${data?.token}?system=${account.measurementSystem}`;
    const deeplinkLabel = isEventTab ? 'Deeplink' : (
      <LogInlineLink to={{ pathname: deeplink }} target="_blank" text="Deeplink" isMobile={isMobile} />
    );
    const alertConfirmationLabel = `"${data?.value}"`;
    const alertConfirmationAlertLabel = `"${data?.alert?.label}"`;
    const alertConfirmationAlertTimestamp = data?.mqttMessageTimestamp ?
      momentFormat(data?.mqttMessageTimestamp, account, isThisYear(new Date(data?.mqttMessageTimestamp)) ? 'dateTimePartial' : 'dateTimeShort') : '';

    const format = getDateTimeFormat(account.measurementSystem, 'dateTimePartial');
    const messageTimestamp = log.messageTimestamp ? dateFormat(new Date(log.messageTimestamp), format) : '';
    switch (eventId) {
      case CREATE_SHIPMENT:
        return (
          <>
            Shipment
            {' '}
            {shipmentLabel}
            {' '}
            was created
          </>
        );
      case EDIT_SHIPMENT:
        return (
          <>
            Shipment
            {' '}
            {shipmentLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_SHIPMENT:
        return (
          <>
            Shipment
            {' '}
            {shipmentLabel}
            {' '}
            was removed
          </>
        );
      case STOP_SHIPMENT:
        return (
          <>
            Shipment
            {' '}
            {shipmentLabel}
            {' '}
            was stopped
          </>
        );
      case DONE_SHIPMENT:
        return (
          <>
            Shipment
            {' '}
            {shipmentLabel}
            {' '}
            was completed
          </>
        );
      case CREATE_DEEPLINK:
        return (
          <>
            {deeplinkLabel}
            {' '}
            was created for shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case NOT_IN_TIME_SHIPMENT:
        return (
          <>
            Shipment
            {' '}
            {shipmentLabel}
            {' '}
            was not completed in time
          </>
        );
      case ADD_DEVICE:
        return (
          <>
            Device
            {' '}
            {deviceLabel}
            {' '}
            was added to shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case REMOVE_DEVICE:
        return (
          <>
            Device
            {' '}
            {deviceLabel}
            {' '}
            was removed from shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case ADD_DELIVERY:
        return (
          <>
            Delivery checkpoint #
            {delivery.index + 1}
            {' '}
            was added to shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case EDIT_DELIVERY:
        return (
          <>
            Delivery checkpoint #
            {delivery.index + 1}
            {' '}
            was edited in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case REMOVE_DELIVERY:
        return (
          <>
            Delivery checkpoint #
            {delivery.index + 1}
            {' '}
            was removed from shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case DONE_CHECKPOINT:
        return (
          <>
            Delivery checkpoint #
            {delivery.index + 1}
            {' '}
            was completed in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case NOT_IN_TIME_CHECKPOINT:
        return (
          <>
            Delivery checkpoint #
            {delivery.index + 1}
            {' '}
            was not completed in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case ADD_CARGO:
        return (
          <>
            Cargo
            {' '}
            {cargoTitle}
            {' '}
            was added to shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case EDIT_CARGO:
        return (
          <>
            Cargo
            {' '}
            {cargoTitle}
            {' '}
            was edited in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case REMOVE_CARGO:
        return (
          <>
            Cargo
            {' '}
            {cargoTitle}
            {' '}
            was removed from shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case UPLOAD_FILE:
        return (
          <>
            File
            {' '}
            {fileName}
            {' '}
            was uploaded to shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case REMOVE_FILE:
        return (
          <>
            File
            {' '}
            {fileName}
            {' '}
            was removed from shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case ADD_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was added to shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case REMOVE_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was removed from shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case TRIGGER_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was triggered
            {' '}
            {triggeredInShipmentLabel}
            {' '}
            by
            {' '}
            {deviceLabel}
            {' '}
            on
            {' '}
            {messageTimestamp}
          </>
        );
      case ASSIGN_ALERT_TO_DEVICE:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was assigned to device
            {' '}
            {deviceLabel}
            {' '}
            in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case UNASSIGN_ALERT_FROM_DEVICE:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was unassigned from device
            {' '}
            {deviceLabel}
            {' '}
            in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case ADD_NOTIFICATION:
        return (
          <>
            Recipient
            {' '}
            {recipientName}
            {' '}
            was added
            {' '}
            to shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case EDIT_NOTIFICATION:
        return (
          <>
            Recipient
            {' '}
            {recipientName}
            {' '}
            was changed
            {' '}
            in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case REMOVE_NOTIFICATION:
        return (
          <>
            Recipient
            {' '}
            {recipientName}
            {' '}
            was removed
            {' '}
            from shipment
            {' '}
            in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case ADD_ALERT_CONFIRMATION:
        return (
          <>
            Verification
            {' '}
            {alertConfirmationLabel}
            {' '}
            was added for alert
            {' '}
            {alertConfirmationAlertLabel}
            {' '}
            triggered on
            {' '}
            {alertConfirmationAlertTimestamp}
            {' '}
            in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case EDIT_ALERT_CONFIRMATION:
        return (
          <>
            Verification
            {' '}
            {alertConfirmationLabel}
            {' '}
            was edited for alert
            {' '}
            {alertConfirmationAlertLabel}
            {' '}
            triggered on
            {' '}
            {alertConfirmationAlertTimestamp}
            {' '}
            in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      case REMOVE_ALERT_CONFIRMATION:
        return (
          <>
            Verification was withdrawn from alert
            {' '}
            {alertConfirmationAlertLabel}
            {' '}
            triggered on
            {' '}
            {alertConfirmationAlertTimestamp}
            {' '}
            in shipment
            {' '}
            {shipmentLabel}
          </>
        );
      default:
        return 'Not recognized event';
    }
  } catch (e) {
    return 'Not recognized event';
  }
};

export default EventDescription;
